/* eslint-disable */
/* eslint-disable no-console */
/* eslint-disable sonarjs/no-duplicate-string */
import React, { useState } from 'react';
import {
    Grid,
    Typography,
    RACModalCard,
    RACButton,
    RACRadio,
    RACTextbox,
    CircularProgress,
} from '@rentacenter/racstrap';
import { agreementGlobalStyles } from '../../agreementGlobalStyles';
import { useTranslation } from 'react-i18next';
import { makeApiClientCall } from '../../../api/user';
import { appConfig } from '../../../config/app-config';
import {
    CustomerPromotionProfile,
    RewardFaceValue,
    validateEmail,
    validatePhoneNumber,
    phoneNumberFormat,
    stripPhoneNumber,
    buildEnrollPayload,
    buildEmailPayload,
    buildTextPayload,
    needsEnrollment,
} from './RAFUtils';

interface SendReferralLinkInterface {
    customerId: string;
    customerName: string;
    customerFirstName: string;
    customerLastName: string;
    email: string;
    phoneNumber: string;
    globalCustomerId: string;
    state?: string;
    customerPromotionProfile: CustomerPromotionProfile[];
    rewardFaceValue?: RewardFaceValue;
    sendReferralLinkCompletedFn: () => void;
    sendReferralLinkCancelledFn: () => void;
    onSuccess: () => void;
    onError: (message: string) => void;
    setCustomerPromotionProfile: (profile: any[]) => void;
}

export default function SendReferralLink(props: SendReferralLinkInterface) {
    const { t } = useTranslation();
    const {
        customerId,
        customerFirstName,
        customerLastName,
        email,
        phoneNumber,
        globalCustomerId,
        state,
        customerPromotionProfile,
        rewardFaceValue,
        sendReferralLinkCompletedFn,
        sendReferralLinkCancelledFn,
        onSuccess,
        onError,
        setCustomerPromotionProfile,
    } = props;

    const classes = agreementGlobalStyles();

    const [sendReferralLinkInput, setSendReferralLinkInput] = useState({
        isEmailSelected: true,
        isTextSelected: false,
        emailTextValue: email || '',
        phoneTextValue: phoneNumber || '',
    });

    const [isValidEmailError, setIsValidEmailError] = useState(false);
    const [isValidPhoneNumError, setIsValidPhoneNumError] = useState(false);
    const [linkLoader, setLinkLoader] = useState(false);

    const handleInputChange = (field: string, value: string) => {
        if (field === 'Email') {
            setIsValidEmailError(false);
            setSendReferralLinkInput({ ...sendReferralLinkInput, emailTextValue: value });
        } else if (field === 'Text') {
            setIsValidPhoneNumError(false);
            setSendReferralLinkInput({ ...sendReferralLinkInput, phoneTextValue: value });
        }
    };

    const handleRadioChange = (field: string) => {
        if (field === 'Email') {
            setIsValidEmailError(false);
            setSendReferralLinkInput({
                ...sendReferralLinkInput,
                isEmailSelected: !sendReferralLinkInput.isEmailSelected,
                isTextSelected: false,
            });
        } else if (field === 'Text') {
            setIsValidPhoneNumError(false);
            setSendReferralLinkInput({
                ...sendReferralLinkInput,
                isTextSelected: !sendReferralLinkInput.isTextSelected,
                isEmailSelected: false,
            });
        }
    };

    const buildPayload = () => {
        const storeNumber = window.sessionStorage.getItem("storeNumber");
        const emailAddress = sendReferralLinkInput.emailTextValue;
        const phoneNum = stripPhoneNumber(sendReferralLinkInput.phoneTextValue);

        if (needsEnrollment(customerPromotionProfile)) {
            return buildEnrollPayload({
                customerPromotionProfile,
                globalCustomerId,
                isEmailSelected: sendReferralLinkInput.isEmailSelected,
                isTextSelected: sendReferralLinkInput.isTextSelected,
                storeNumber,
            });
        }

        if (sendReferralLinkInput.isEmailSelected) {
            return buildEmailPayload({
                customerPromotionProfile,
                customerFirstName,
                customerLastName,
                state: state || '',
                phoneNumber,
                emailAddress,
                globalCustomerId,
            });
        }

        return buildTextPayload({
            customerPromotionProfile,
            customerFirstName,
            customerLastName,
            phoneNum,
            customerId,
            globalCustomerId,
            rewardFaceValue,
        });
    };

    const handleSend = async () => {
        try {
            const payload = buildPayload();

            setLinkLoader(true);
            const response = await makeApiClientCall({
                url: 'advocate/enroll',
                method: 'POST',
                payload,
                baseUrl: appConfig.apiUrls.customer,
            });
            setLinkLoader(false);

            if (response?.status === 200) {
                if (payload?.isEnroll) {
                    setCustomerPromotionProfile([]);
                }
                onSuccess();
                sendReferralLinkCompletedFn();
            } else {
                const errorMsg = response?.data?.errors?.[0]?.error ||
                    'An error occurred while processing your request. Please try again later.';
                onError(errorMsg);
            }
        } catch (error) {
            setLinkLoader(false);
            onError('An error occurred while processing your request. Please try again later.');
        }
    };

    const handleSendClick = () => {
        if (sendReferralLinkInput.isEmailSelected) {
            if (validateEmail(sendReferralLinkInput.emailTextValue)) {
                handleSend();
            } else {
                setIsValidEmailError(true);
            }
        } else if (sendReferralLinkInput.isTextSelected) {
            if (validatePhoneNumber(sendReferralLinkInput.phoneTextValue)) {
                handleSend();
            } else {
                setIsValidPhoneNumError(true);
            }
        }
    };

    const isSendDisabled = () => {
        if (linkLoader) return true;
        if (sendReferralLinkInput.isEmailSelected) {
            return !validateEmail(sendReferralLinkInput.emailTextValue);
        }
        return stripPhoneNumber(sendReferralLinkInput.phoneTextValue).length < 10;
    };

    return (
        <RACModalCard
            title={t('Send Referral Link')}
            isOpen={true}
            borderRadius={'25px !important'}
            closeIcon={true}
            maxWidth="sm"
            onClose={() => {
                if (!linkLoader) {
                    sendReferralLinkCancelledFn();
                }
            }}
        >
            <Grid className={classes.p3}>
                <Grid container>
                    <Grid item md={12} className={classes.mb3}>
                        <Typography
                            component="label"
                            className={`${classes.formLabel} ${classes.w100} ${classes.fontBold}`}
                        >
                            {t('How do you want the referral link to be shared?')}
                        </Typography>
                    </Grid>
                    <Grid item md={12} className={classes.mb3}>
                        <RACRadio
                            value={sendReferralLinkInput.isEmailSelected}
                            checked={sendReferralLinkInput.isEmailSelected}
                            onChange={() => handleRadioChange('Email')}
                            label={t('Email')}
                        />
                        <RACRadio
                            value={sendReferralLinkInput.isTextSelected}
                            checked={sendReferralLinkInput.isTextSelected}
                            onChange={() => handleRadioChange('Text')}
                            label={t('Text')}
                        />
                    </Grid>
                    <Grid item md={6}>
                        {sendReferralLinkInput.isEmailSelected ? (
                            <div>
                                <RACTextbox
                                    inputlabel={t('Email')}
                                    inputLabelClassname="form-label"
                                    name="Email"
                                    value={sendReferralLinkInput.emailTextValue}
                                    OnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        handleInputChange('Email', e.target.value)
                                    }
                                    isCurrency={false}
                                    type="email"
                                    disabled={linkLoader}
                                />
                                {isValidEmailError && (
                                    <label style={{ color: 'red', fontSize: 'small', marginLeft: '3%' }}>
                                        {t('Please enter valid Email')}
                                    </label>
                                )}
                            </div>
                        ) : (
                            <div>
                                <RACTextbox
                                    inputlabel={t('Phone number')}
                                    inputLabelClassname="form-label"
                                    name="Phone number"
                                    value={phoneNumberFormat(sendReferralLinkInput.phoneTextValue)}
                                    OnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        handleInputChange('Text', e.target.value)
                                    }
                                    isCurrency={false}
                                    type="text"
                                    maxlength={14}
                                    disabled={linkLoader}
                                />
                                {isValidPhoneNumError && (
                                    <label style={{ color: 'red', fontSize: 'small', marginLeft: '3%' }}>
                                        {t('Please enter valid phone number')}
                                    </label>
                                )}
                            </div>
                        )}
                    </Grid>
                </Grid>
                <Grid item md={12} className={`${classes.pb4} ${classes.px3} ${classes.textRight}`}>
                    <RACButton
                        variant="outlined"
                        color="primary"
                        className={classes.mx1}
                        onClick={sendReferralLinkCancelledFn}
                        disabled={linkLoader}
                    >
                        {t('Cancel')}
                    </RACButton>
                    <RACButton
                        variant="contained"
                        color="primary"
                        className={classes.mx1}
                        onClick={handleSendClick}
                        disabled={isSendDisabled()}
                    >
                        {linkLoader ? <CircularProgress size={20} style={{ color: 'white' }} /> : t('Send')}
                    </RACButton>
                </Grid>
            </Grid>
        </RACModalCard>
    );
}
